import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import ukLocale from '@angular/common/locales/uk';
import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { AuthEffects, AuthInterceptor, AuthService, authReducer } from '@lib/auth';
import { medicalDocumentsReducer } from '@lib/medical-documents';
import { memberHubReducer } from '@lib/members/data-access';
import { MEMBERS_API_URL, fetchMemberAppointmentsEffect, fetchMemberFranchiseEffect, fetchMemberPackageEffect, fetchMemberProfileEffect } from '@lib/members/data-access';
import { provideMembersLibConfig } from '@lib/members/feature';
import {
  addPrescriptionDraftEffect,
  clearPrescriptionDraftsEffect,
  deletePrescriptionDraftEffect,
  fetchPrescriptionsEffect,
  loadPrescriptionDraftsFromStorageEffect,
  prescriptionsFeatureReducer,
  processPrescriptionsDraftsEffect,
  resetPrescriptionsStateEffect,
  updatePrescriptionDraftEffect,
} from '@lib/prescriptions/data-access';
import { ADMIN_PORTAL_URL } from '@lib/tasks';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { initializeApp } from 'firebase/app';
import { getMessaging } from 'firebase/messaging';
import { getRemoteConfig } from 'firebase/remote-config';

import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

import { hmReducer } from '@hm/core/stores/hm/hm.reducer';

import { environment } from '@hm/environments/environment';

import { FeatureFlagService } from '@lib/core/services/feature-flag.service';
import { PortalType, UserProfileResolverService } from '@lib/core/services/user-profile-resolver.service';
import { UserEffects } from '@lib/core/stores/user/user.effects';
import { userReducer } from '@lib/core/stores/user/user.reducer';
import { API_URL, AUTH_API_URL, PROTECTED_API_URL } from '@lib/core/tokens/api.tokens';
import { FIREBASE_API_URL, FIREBASE_CONFIG, FIREBASE_MESSAGING } from '@lib/core/tokens/firebase.tokens';
import { provideTranslateModule } from '@lib/core/translate/translate.provider';

import { MD_API_URL } from '@lib/medical-documents/core/tokens/medical-documents.tokens';

import * as medicalDocumentsEffects from '../../../../packages/medical-documents/src/core/store/medical-documents.effects';
import { provideChatLibConfig } from '../core/providers/provide-chat-config.function';
import * as hmEffects from './../core/stores/hm/hm.effects';
import { routes } from './app.routes';
import { healthManagerMembersConfig } from '../core/providers/members-lib.config';

registerLocaleData(ukLocale);

export const firebaseApp = initializeApp(environment.firebase);
export const messaging = getMessaging(firebaseApp);
const remoteConfig = getRemoteConfig(firebaseApp);
remoteConfig.settings = {
  fetchTimeoutMillis: 0,
  minimumFetchIntervalMillis: 0,
};

function initializeTokens(authService: AuthService) {
  return () => authService.initializeTokens();
}

export function initializeFeatureFlags(featureFlagService: FeatureFlagService) {
  return () => featureFlagService.initialize();
}

export function initializeUserProfile(userProfileResolver: UserProfileResolverService) {
  return () => userProfileResolver.resolveAndSetUserProfile(PortalType.HEALTH_MANAGER).toPromise();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideStore({
      auth: authReducer,
      hm: hmReducer,
      user: userReducer,
      prescriptions: prescriptionsFeatureReducer,
      memberHub: memberHubReducer,
      medicalDocuments: medicalDocumentsReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideEffects([AuthEffects, hmEffects, medicalDocumentsEffects, UserEffects]),
    provideEffects({
      loadPrescriptionDraftsFromStorage: loadPrescriptionDraftsFromStorageEffect,
      addPrescriptionDraft: addPrescriptionDraftEffect,
      updatePrescriptionDraft: updatePrescriptionDraftEffect,
      deletePrescriptionDraft: deletePrescriptionDraftEffect,
      clearPrescriptionDrafts: clearPrescriptionDraftsEffect,
      processPrescriptionsDrafts: processPrescriptionsDraftsEffect,
      resetPrescriptionsState: resetPrescriptionsStateEffect,
      fetchPrescriptions: fetchPrescriptionsEffect,
    }),
    // Use shared member hub effects with correct API_URL injected
    provideEffects({
      fetchMemberProfile: fetchMemberProfileEffect,
      fetchMemberFranchise: fetchMemberFranchiseEffect,
      fetchMemberPackage: fetchMemberPackageEffect,
      fetchMemberAppointments: fetchMemberAppointmentsEffect,
    }),
    provideHttpClient(withInterceptors([AuthInterceptor])),
    provideTranslateModule(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTokens,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlags,
      deps: [FeatureFlagService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeUserProfile,
      deps: [UserProfileResolverService],
      multi: true,
    },
    { provide: AUTH_API_URL, useValue: environment.authorizationApiUrl },
    { provide: API_URL, useValue: environment.healthManagerApiUrl },
    { provide: PROTECTED_API_URL, useValue: environment.protectedApiUrl },
    { provide: FIREBASE_API_URL, useValue: environment.healthManagerApiUrl },
    { provide: FIREBASE_CONFIG, useValue: environment.firebase },
    { provide: FIREBASE_MESSAGING, useValue: messaging },
    { provide: MD_API_URL, useValue: environment.protectedApiUrl },
    { provide: ADMIN_PORTAL_URL, useValue: environment.adminPortalUrl },
    { provide: MEMBERS_API_URL, useValue: environment.healthManagerApiUrl },
    { provide: LOCALE_ID, useValue: 'uk' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideNativeDateAdapter(),
    provideChatLibConfig(),
    { provide: MEMBERS_API_URL, useValue: environment.healthManagerApiUrl },
    provideMembersLibConfig(healthManagerMembersConfig),
  ],
};
